// Cross-site source attribution (Tier 0).
//
// The DataViking properties (dataviking.tech, synthpanel.dev, synthbench.org,
// traitprint.com) each report to a *separate* PostHog project on a separate
// cookie domain, so native session stitching is impossible. To attribute a
// cross-site referral we stamp outbound links to sibling properties with
// canonical UTMs; the destination project can then read `utm_source` even when
// the HTTP referrer is stripped (the common case for cross-origin navigations).
//
// The static marketing sites rewrite every sibling href once at DOMContentLoaded.
// Traitprint is a single-page app whose links render and re-render constantly,
// so instead we stamp the *clicked* anchor just-in-time via capture-phase
// listeners:
//   - `pointerdown` covers mouse / touch / pen activation AND context-menu
//     "open in new tab" / "copy link" (the menu opens after pointerdown).
//   - `click` covers keyboard activation (Enter on a focused link), which fires
//     no pointer event.
// Stamping is idempotent (a link that already carries `utm_source` is left
// alone), so the two listeners never double-tag.

const SIBLING_DOMAINS = ["dataviking.tech", "synthpanel.dev", "synthbench.org", "traitprint.com"];
const SOURCE = "traitprint";

function bareHost(hostname: string): string {
  return hostname.replace(/^www\./, "").toLowerCase();
}

function isSibling(host: string): boolean {
  return SIBLING_DOMAINS.some((domain) => host === domain || host.endsWith(`.${domain}`));
}

/**
 * Given a link's href and the current page URL, return the href rewritten with
 * cross-site UTMs, or `null` if the link should be left untouched — i.e. it is
 * internal (same registrable host), not a sibling property, not http(s),
 * unparseable, or already carries a `utm_source`.
 *
 * Pure and side-effect free so the decision logic is unit-testable without a DOM.
 */
export function crossSiteHref(href: string, currentUrl: string): string | null {
  let url: URL;
  let current: URL;
  try {
    current = new URL(currentUrl);
    url = new URL(href, currentUrl);
  } catch {
    return null;
  }

  if (url.protocol !== "http:" && url.protocol !== "https:") return null;

  const host = bareHost(url.hostname);
  if (host === bareHost(current.hostname)) return null; // internal navigation
  if (!isSibling(host)) return null; // not a DataViking property
  if (url.searchParams.has("utm_source")) return null; // already attributed

  url.searchParams.set("utm_source", SOURCE);
  url.searchParams.set("utm_medium", "referral");
  url.searchParams.set("utm_campaign", "cross-site");
  return url.toString();
}

function stampAnchor(anchor: HTMLAnchorElement): void {
  const href = anchor.getAttribute("href");
  if (!href) return;
  const next = crossSiteHref(href, window.location.href);
  if (next) anchor.setAttribute("href", next);
}

function handleActivation(event: Event): void {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const anchor = target.closest("a[href]");
  if (anchor instanceof HTMLAnchorElement) stampAnchor(anchor);
}

let installed = false;

/**
 * Install the capture-phase listeners that stamp outbound sibling-property
 * links. Idempotent and a no-op outside the browser. Runs in every environment
 * (it only ever acts on links pointing at the other DataViking domains, which
 * do not exist on localhost / preview hosts).
 */
export function initCrossSiteAttribution(): void {
  if (installed || typeof document === "undefined") return;
  installed = true;
  document.addEventListener("pointerdown", handleActivation, { capture: true });
  document.addEventListener("click", handleActivation, { capture: true });
}
